import { SingleValue } from "react-select"

import CustomInput from "../../../component/CustomInput/CustomInput"
import CustomSelect from "../../../component/customSelect/customSelect"
import { GRADES } from "../../../utils/grade"
import { complexities } from "../../../utils/complexity"
import { normalizeYearInput } from "../../../utils/yearValidation"
import { SubjectOption, SelectOption } from "../hooks/useQuestionSubjects"
import { QuestionInlineDraft } from "../types/questionDraft"
import LevelSelectors, {
  EMPTY_LEVEL_VALUES,
  LevelFieldErrors,
} from "./LevelSelectors"

type QuestionDetailSidebarEditableProps = {
  draft: QuestionInlineDraft
  onChange: (updater: (prev: QuestionInlineDraft) => QuestionInlineDraft) => void
  subjects: SubjectOption[]
  fieldErrors?: {
    subject?: string
    levels?: LevelFieldErrors
    year?: string
  }
  onSubjectChange?: () => void
  onLevelChange?: () => void
}

const QuestionDetailSidebarEditable = ({
  draft,
  onChange,
  subjects,
  fieldErrors = {},
  onSubjectChange,
  onLevelChange,
}: QuestionDetailSidebarEditableProps) => (
  <aside className="question-detail__sidebar">
    <h3>Metadata</h3>

    <div className="question-detail__edit-grid">
      <div className="field">
        <label>Subject</label>
        <CustomSelect
          placeholder="Select subject"
          data={subjects}
          value={draft.subject}
          onChange={(value: SingleValue<SubjectOption>) => {
            onSubjectChange?.()
            onChange((prev) => ({
              ...prev,
              subject: value ?? null,
              levels: { ...EMPTY_LEVEL_VALUES },
              levelNames: {},
            }))
          }}
          error={fieldErrors.subject}
        />
      </div>

      {draft.subject?.value && (
        <LevelSelectors
          subjectId={draft.subject.value}
          levelLabels={draft.subject.level_labels}
          values={draft.levels}
          names={draft.levelNames}
          onChange={(next, nextNames) => {
            onLevelChange?.()
            onChange((prev) => ({
              ...prev,
              levels: next,
              levelNames: nextNames,
            }))
          }}
          errors={fieldErrors.levels}
          className="field"
        />
      )}

      <div className="field">
        <label>Level</label>
        <CustomSelect
          placeholder="Select level"
          data={GRADES}
          value={draft.grade}
          onChange={(value: SingleValue<SelectOption>) => {
            onChange((prev) => ({ ...prev, grade: value ?? null }))
          }}
        />
      </div>

      <div className="field">
        <label>Difficulty</label>
        <CustomSelect
          placeholder="Select difficulty"
          data={complexities}
          value={draft.complexity}
          onChange={(value: SingleValue<SelectOption>) => {
            onChange((prev) => ({ ...prev, complexity: value ?? null }))
          }}
        />
      </div>

      <div className="field">
        <label>Source</label>
        <CustomInput
          value={draft.source}
          placeholder="e.g. ORIGINAL"
          onChange={(event) =>
            onChange((prev) => ({ ...prev, source: event.target.value }))
          }
        />
      </div>

      <div className="field">
        <label>Year</label>
        <CustomInput
          value={draft.year}
          placeholder="e.g. 2026"
          onChange={(event) =>
            onChange((prev) => ({
              ...prev,
              year: normalizeYearInput(event.target.value),
            }))
          }
          error={fieldErrors.year}
        />
      </div>

      <div className="field">
        <label>Marks</label>
        <CustomInput
          value={draft.marks}
          placeholder="e.g. 20"
          onChange={(event) =>
            onChange((prev) => ({ ...prev, marks: event.target.value }))
          }
        />
      </div>

      <div className="field">
        <label htmlFor="inline-sub-parts">Sub-parts</label>
        <textarea
          id="inline-sub-parts"
          className="question-detail__sub-parts-input"
          placeholder="e.g. a, b, c"
          value={draft.subPartsText}
          onChange={(event) =>
            onChange((prev) => ({ ...prev, subPartsText: event.target.value }))
          }
          title="Sub-part labels separated by commas"
        />
      </div>
    </div>
  </aside>
)

export default QuestionDetailSidebarEditable
